import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useAppDispatch } from "../../../store";
import { setCurrentPage } from "../../../store/auth/authReducer";
import Button from "@mui/material/Button";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography, Stack, Switch, Card, CardContent, Chip, Paper, Tooltip, IconButton, Collapse } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InboxIcon from '@mui/icons-material/Inbox';
import CodeIcon from "@mui/icons-material/Code";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import axios from "axios";
import { logOut } from "../../../utils/logOut";
import { styled } from "@mui/system";
import "./VirtualAssistant.css";
import TranslatedText from "../../../utils/Translate";
import { useSnackbar } from "../../../utils/SnackbarContext";
import HtmlInShadow from "../../../utils/HtmlInShadow";
import NoDataOverlay from "../../../utils/NoDataOverlay";
import Markdown from "../../../utils/Markdown";
import { log } from "console";
import { useSelector } from "react-redux";

type HistoryMessage = { role: "system" | "user" | "assistant"; content: string };

type BlockType = "user_prompt" | "function_call" | "function_result" | "usage";

interface BlockData {
    type: BlockType;
    content: string;
    functionName?: string;
    timestamp: string;
    id: string;
}

const ChatContainer = styled("div")({
    display: "flex",
    width: "90%",
    minWidth: "800px",
    maxWidth: "1800px",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
});
const MainContainer = styled("div")({
    display: "flex",
    flexDirection: "row",
    gap: 16,
    flex: 1,
    minHeight: 0,
});
const ChatSection = styled("div")({
    flex: 2,
    display: "flex",
    flexDirection: "column",
    minWidth: "400px",
    minHeight: 0,
});

const COLLAPSED_W = 70;

const BlocksSection = styled("div")<{ collapsed: boolean }>(({ collapsed, theme }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",                // <- ключевое: заполняет высоту родителя
    minHeight: 0,                  // <- чтобы overflow внутри работал
    minWidth: collapsed ? `${COLLAPSED_W}px` : "300px",
    maxWidth: collapsed ? `${COLLAPSED_W}px` : "500px",
    width: collapsed ? `${COLLAPSED_W}px` : "500px",
    // transition: "width 220ms ease",
    position: "relative",
    overflowY: "auto",
    backgroundColor: theme.palette.grey[50],
    borderRadius: 3,// фон на всю колонку
}));

const BlocksContainer = styled(Paper)(({ theme }) => ({
    padding: 16,
    flex: 1,
    backgroundColor: theme.palette.grey[50],
    "& > .va-anim": {
        opacity: 0,
        transform: "translateY(8px)",
        transition: "opacity 300ms ease, transform 300ms ease",
    },
    "& > .va-anim.va-in": {
        opacity: 1,
        transform: "translateY(0)",
    },
}));

const colorForBlockType = (t: BlockType, theme: any) => {
    switch (t) {
        case "user_prompt": return theme.palette.warning.main;
        case "function_call": return theme.palette.info.main;
        case "function_result": return theme.palette.success.main;
        case "usage": return theme.palette.primary.main;
        default: return theme.palette.text.primary;
    }
};
const BlocksHeader = styled(Box)<{ collapsed: boolean }>(({ theme, collapsed }) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    // padding: "8px",
    // borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: collapsed ? "center" : "space-between",
}));
const CollapsedBar = styled(Paper)(({ theme }) => ({
    width: COLLAPSED_W,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 8,
    gap: 8,
    overflowY: "auto",
    backgroundColor: theme.palette.grey[50],
}));
const ChatMessagesRoot = styled("div")({
    padding: "0 20px",
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    "& > *": {
        opacity: 0,
        transition: "opacity 0.3s ease-in-out",
    },
});
const MessageContainer = styled("div")({
    marginBottom: 16,
    "&.scroll-animation": { opacity: 1, transform: "translateY(0)" },
});
const InputContainer = styled("div")({
    display: "flex",
    alignItems: "center",
    padding: 16,
    gap: 12,
});
const BlockCard = styled(Card)(({ theme }) => ({
    marginBottom: "12px",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
}));
const FunctionCallCard = styled(BlockCard)(({ theme }) => ({
    borderLeft: `4px solid ${theme.palette.info.main}`,
}));
const FunctionResultCard = styled(BlockCard)(({ theme }) => ({
    borderLeft: `4px solid ${theme.palette.success.main}`,
}));
const UserPromptCard = styled(BlockCard)(({ theme }) => ({
    borderLeft: `4px solid ${theme.palette.warning.main}`,
}));
const UsageCard = styled(BlockCard)(({ theme }) => ({
    borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

type AIModel = { id: string; name: string };

const MemoChatMessages = React.memo(function MemoChatMessages(props: {
    messages: { id: number; text: string; sender: "user" | "bot" }[];
    parseHTML: boolean;
    chatRef: React.RefObject<HTMLDivElement>;
}) {
    const { messages, parseHTML, chatRef } = props;
    return (
        <ChatMessagesRoot ref={chatRef}>
            {messages.length === 0 ? (
                <Box
                    sx={{
                        opacity: 1,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <NoDataOverlay text="No messages yet" />
                </Box>
            ) : (
                messages.map((message, idx) => (
                    <MessageContainer
                        key={idx}
                        className="scroll-animation"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: message.sender === "user" ? "flex-end" : "flex-start",
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                mb: 0.5,
                                color: "text.secondary",
                                alignSelf: message.sender === "user" ? "flex-end" : "flex-start",
                            }}
                        >
                            {message.sender === "user" ? "You" : "Bot"}
                        </Typography>
                        <Box
                            sx={{
                                maxWidth: "70%",
                                p: 1.5,
                                borderRadius: 3,
                                bgcolor: message.sender === "user" ? "primary.main" : "grey.100",
                                color: message.sender === "user" ? "white" : "text.primary",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            {parseHTML ? (
                                <HtmlInShadow html={message.text} />
                            ) : (
                                <Markdown text={message.text} />
                            )}
                        </Box>
                    </MessageContainer>
                ))
            )}
        </ChatMessagesRoot>
    );
});

export default function VirtualAssistant() {
    const dispatch = useAppDispatch();
    const { showSnackbar } = useSnackbar();

    useEffect(() => {
        dispatch(setCurrentPage(`Virtual Assistant`));
    }, [dispatch]);

    const partner = useSelector(
        (state: any) => state.auth.authData.partner
    )

    // --- NEW! SPLIT chatConfig and history ---
    const [aimodels, setAImodels] = useState<AIModel[]>([]);
    const [aiprompts, setAIprompts] = useState([]);
    const chatRef = useRef<HTMLDivElement | null>(null);
    const blocksRef = useRef<HTMLDivElement | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [parseHTML, setParseHTML] = useState(false);
    const [blocks, setBlocks] = useState<BlockData[]>([]);
    const [prompt, setPrompt] = useState<string>("");
    const [credentials, setCredentials] = useState<any>({});

    const [chatHistory, setChatHistory] = useState<HistoryMessage[]>(() => {
        const savedHistory = localStorage.getItem("va_history") || "[]";
        try { return JSON.parse(savedHistory); } catch { return []; }
    });

    const [chatConfig, setChatConfig] = useState<{
        aimodel: string;
        aiprompt?: string;
        mode: "message" | "stream";
    }>(() => {
        const cfg = localStorage.getItem("va_config");
        if (cfg) {
            const json = JSON.parse(cfg);
            return {
                aimodel: json.aimodel || "",
                aiprompt: json.aiprompt || "",
                mode: "stream",
            };
        }
        return { aimodel: "", aiprompt: "", mode: "stream" };
    });

    const [isBlocksCollapsed, setIsBlocksCollapsed] = useState<boolean>(() => {
        const saved = localStorage.getItem("va_blocks_collapsed");
        return saved ? saved === "1" : true;
    });

    const saveConfig = useCallback(() => {
        localStorage.setItem('va_config', JSON.stringify(chatConfig));
    }, [chatConfig]);
    // history — отдельно!
    useEffect(() => {
        localStorage.setItem("va_history", JSON.stringify(chatHistory));
    }, [chatHistory]);
    useEffect(() => {
        localStorage.setItem("va_blocks_collapsed", isBlocksCollapsed ? "1" : "0");
    }, [isBlocksCollapsed]);
    useEffect(() => {
        // если настроек не было и модели уже подгрузились — выбираем первую
        if (!localStorage.getItem('va_config') && aimodels.length > 0) {
            setChatConfig(prev => ({ ...prev, aimodel: aimodels[0].name }));
        }
    }, [aimodels]);

    // Автоскролл чата
    useEffect(() => {
        if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }, [chatHistory]);
    // Автоскролл блоков
    useEffect(() => {
        if (blocksRef.current) blocksRef.current.scrollTop = blocksRef.current.scrollHeight;
    }, [blocks]);

    // Мемоизированные message-объекты
    const messages = useMemo(() =>
        chatHistory
            .filter((item) => item.role === "user" || item.role === "assistant")
            .map((item, idx) => ({
                id: idx,
                text: item.content,
                sender: item.role === "user" ? "user" : "bot" as "user" | "bot"
            }))
        , [chatHistory]);

    const iconForBlockType = (t: BlockType) => {
        switch (t) {
            case "user_prompt": return <ChatBubbleOutlineIcon fontSize="small" />;
            case "function_call": return <CodeIcon fontSize="small" />;
            case "function_result": return <CheckCircleOutlineIcon fontSize="small" />;
            case "usage": return <BarChartIcon fontSize="small" />;
            default: return <ChatBubbleOutlineIcon fontSize="small" />;
        }
    };

    // --- Handlers ---
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setPrompt(e.target.value);
    const handleClearHistory = () => {
        setChatHistory([]);
        setBlocks([]);
        localStorage.removeItem("va_history");
    };

    // Format Json
    const formatJson = (content: string): string => {
        try {
            const maybe = JSON.parse(content);
            if (typeof maybe === "string") {
                try { return JSON.stringify(JSON.parse(maybe), null, 2); } catch { }
            }
            return JSON.stringify(maybe, null, 2);
        } catch {
            return content;
        }
    };

    const appearedIdsRef = React.useRef<Set<string>>(new Set());
    function Appear({ id, children, delay = 0 }: { id: string; children: React.ReactNode; delay?: number; }) {
        const ref = React.useRef<HTMLDivElement | null>(null);
        const alreadyAppeared = appearedIdsRef.current.has(id);
        React.useEffect(() => {
            if (alreadyAppeared) return;
            const el = ref.current;
            if (!el) return;
            el.style.transitionDelay = `${delay}ms`;
            const raf = requestAnimationFrame(() => {
                el.classList.add("va-in");
                appearedIdsRef.current.add(id);
            });
            return () => cancelAnimationFrame(raf);
        }, [id, delay, alreadyAppeared]);
        const className = alreadyAppeared ? undefined : "va-anim";
        return (
            <div ref={ref} className={className} style={{ marginBottom: 12 }}>
                {children}
            </div>
        );
    }

    // MESSAGE SEND
    const sendStreamMessage = async (userPrompt: string) => {
        if (abortRef.current) { try { abortRef.current.abort(); } catch { } }
        const controller = new AbortController();
        abortRef.current = controller;
        setIsStreaming(true);
        setBlocks([]);
        setChatHistory(prev => [...prev, { role: "user", content: userPrompt }]);
        if (localStorage.getItem("assistant_unique_token") === null) {
            localStorage.setItem("assistant_unique_token", Date.now().toString() + String(Math.floor(Math.random() * 10_000_000)).padStart(7, '0'));
        }
        const uniqueToken = localStorage.getItem("assistant_unique_token");

        // const requestHistory: HistoryMessage[] = [
        //     ...chatHistory,
        //     { role: "user", content: userPrompt }
        // ];

        let currentBlock: BlockData | null = null;
        let blockContent = "";

        try {
            const resp = await fetch("https://cardgarantsales.com/restapi/virtualassistant", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("access_token"),
                },
                body: JSON.stringify([{
                    aimodel: chatConfig.aimodel,
                    aiprompt: chatConfig.aiprompt,
                    prompt: userPrompt,
                    history: chatHistory,
                    mode: "stream",
                    include_user_prompt: !!chatConfig.aiprompt,
                    include_function_calls: true,
                    include_function_outputs: true,
                    context: {
                        calleridnum: credentials.username || '',
                        partner: partner != 'null' ? partner : null,
                        calleridname: uniqueToken
                    }
                }]),
                signal: controller.signal,
            });
            if (!resp.ok || !resp.body) throw new Error(`Stream error: ${resp.status}`);

            const reader = resp.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buf = "";
            let evtType: string | null = null;
            let evtDataChunks: string[] = [];
            const appendAssistantDelta = (delta: string) => {
                setChatHistory((prev) => {
                    const hist = [...prev];
                    if (hist.length === 0 || hist[hist.length - 1].role !== "assistant") {
                        hist.push({ role: "assistant", content: "" });
                    }
                    const last = hist[hist.length - 1];
                    hist[hist.length - 1] = {
                        ...last,
                        content: (last.content || "") + delta,
                    };
                    return hist;
                });
            };
            const flushEvent = () => {
                if (!evtType && evtDataChunks.length === 0) return;
                const dataStr = evtDataChunks.join("\n");
                evtDataChunks = [];
                if (evtType === "done") { evtType = null; return; }
                if (evtType === "block_end" && currentBlock) {
                    const finalized: BlockData = {
                        ...currentBlock,
                        content: blockContent,
                        timestamp: new Date().toLocaleTimeString(),
                    };
                    setBlocks(prev => [...prev, finalized]);
                    currentBlock = null;
                    blockContent = "";
                    evtType = null;
                    return;
                }
                if (evtType === "block_start") {
                    try {
                        const meta = JSON.parse(dataStr);
                        currentBlock = {
                            type: meta.block_type as BlockType,
                            content: "",
                            functionName: meta.function_name,
                            timestamp: new Date().toLocaleTimeString(),
                            id: Math.random().toString(36).slice(2),
                        };
                        blockContent = "";
                    } catch { }
                    evtType = null;
                    return;
                }
                try {
                    const maybe = JSON.parse(dataStr);
                    if (maybe && typeof maybe === "object" && "completion_tokens" in maybe) {
                        setBlocks(prev => [
                            ...prev,
                            {
                                type: "usage",
                                content: JSON.stringify(maybe, null, 2),
                                timestamp: new Date().toLocaleTimeString(),
                                id: Math.random().toString(36).slice(2),
                            },
                        ]);
                        evtType = null;
                        return;
                    }
                } catch { }
                const handleDelta = (d: string) => {
                    if (!d) return;
                    if (currentBlock) { blockContent += d; }
                    else { appendAssistantDelta(d); }
                };
                if (dataStr.trim() === "{}" || dataStr.trim() === "[DONE]") {
                    evtType = null; return;
                }
                const lines = dataStr.split(/\r?\n/).filter((l) => l.length > 0);
                let parsedAny = false;
                for (const l of lines) {
                    try {
                        const obj = JSON.parse(l);
                        if (obj && typeof obj === "object" && "delta" in obj) {
                            handleDelta(String(obj.delta));
                            parsedAny = true;
                        }
                    } catch { }
                }
                if (!parsedAny) handleDelta(dataStr);
                evtType = null;
            };
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split(/\r?\n/);
                buf = lines.pop() || "";
                for (const line of lines) {
                    if (line.startsWith("event: ")) { flushEvent(); evtType = line.slice(7).trim(); continue; }
                    if (line.startsWith("data:")) { evtDataChunks.push(line.slice(5).replace(/^:\s?/, "").trimStart()); continue; }
                    if (line.trim() === "") { flushEvent(); }
                }
            }
            flushEvent();
        } catch (e: any) {
            if (e?.name !== "AbortError") {
                console.error("Stream error:", e);
                if (e?.message === "Request failed with status code 401") logOut(dispatch);
                else if (e?.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
                else showSnackbar("Stream error", "error");
            }
        } finally {
            setIsStreaming(false);
            abortRef.current = null;
        }
    };

    // Компонент для отображения специальных блоков (всегда раскрыт)
    const renderBlock = (block: BlockData) => {
        let BlockComponent = BlockCard;
        let title = "";
        let color: "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning" = "default";
        switch (block.type) {
            case "user_prompt": BlockComponent = UserPromptCard; title = "Prompt"; color = "warning"; break;
            case "function_call": BlockComponent = FunctionCallCard; title = "Function Call"; color = "info"; break;
            case "function_result": BlockComponent = FunctionResultCard; title = "Function Result"; color = "success"; break;
            case "usage": BlockComponent = UsageCard; title = "Usage Statistics"; color = "primary"; break;
        }
        return (
            <BlockComponent key={block.id}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip label={title} color={color} size="small" />
                            {block.functionName &&
                                <Chip label={block.functionName} variant="outlined" size="small" color={color} />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {block.timestamp}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            mt: 1,
                            p: 1,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            fontFamily: "monospace",
                            fontSize: "0.875rem",
                            overflow: "visible",
                        }}
                    >
                        <pre style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: "1.4",
                        }}>
                            {formatJson(block.content)}
                        </pre>
                    </Box>
                </CardContent>
            </BlockComponent>
        );
    };

    // Отправка по кнопке/ентеру
    const handleSendMessage = async () => {
        saveConfig();
        const userPrompt = prompt.trim();
        if (!userPrompt || !chatConfig.aimodel) return;
        setPrompt("");
        if (chatConfig.mode === "stream") {
            await sendStreamMessage(userPrompt);
            return;
        }
        try {
            setChatHistory((prev) => [...prev, { role: "user", content: userPrompt }]);
            const requestHistory: HistoryMessage[] = [...chatHistory];
            if (localStorage.getItem("assistant_unique_token") === null) {
                localStorage.setItem("assistant_unique_token", Date.now().toString() + String(Math.floor(Math.random() * 10_000_000)).padStart(7, '0'));
            }
            const uniqueToken = localStorage.getItem("assistant_unique_token");
            const response = await axios.post(
                "https://cardgarantsales.com/restapi/virtualassistant",
                [{
                    aimodel: chatConfig.aimodel,
                    aiprompt: chatConfig.aiprompt,
                    prompt: userPrompt,
                    history: requestHistory,
                    mode: "message",
                    include_user_prompt: chatConfig.aiprompt != null && chatConfig.aiprompt !== "",
                    include_function_calls: true,
                    include_function_outputs: true,
                    context: {
                        calleridnum: credentials.username || '',
                        partner: partner,
                        calleridname: uniqueToken
                    }
                }],
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem("access_token"),
                    },
                }
            );
            const botText = response.data?.assistant_message?.content
                ? response.data.assistant_message.content
                : response.data.toString();
            setChatHistory((prev) => [...prev, { role: "assistant", content: botText }]);
        } catch (error: any) {
            if (error.message === "Request failed with status code 401") logOut(dispatch);
            else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
            else showSnackbar("Request error", "error");
        }
    };

    async function getAIModels(params: object = {}) {
        try {
            const response = await axios.get(
                `https://cardgarantsales.com/restapi/aimodels`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("access_token"),
                },
                params: params,
            });
            setAImodels(response.data);

        } catch (error: any) {
            if (error.message === "Request failed with status code 401") logOut(dispatch);
            else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
        }
    }

    async function getCredentials(params: object = {}) {
        try {
            const response = await axios.get(
                `https://cardgarantsales.com/restapi/webrtc`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("access_token"),
                },
                params: params,
            });
            setCredentials(response.data);

        } catch (error: any) {
            if (error.message === "Request failed with status code 401") logOut(dispatch);
            else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
        }
    }

    async function getAIPrompts(params: object = {}) {
        try {
            const response = await axios.get(
                `https://cardgarantsales.com/restapi/aiprompts`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + localStorage.getItem("access_token"),
                },
                params: params,
            });
            setAIprompts(response.data);
        } catch (error: any) {
            if (error.message === "Request failed with status code 401") logOut(dispatch);
            else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
        }
    }
    useEffect(() => { getAIModels(); getAIPrompts(); getCredentials(); }, []);
    useEffect(() => {
        return () => {
            if (abortRef.current) try { abortRef.current.abort(); } catch { }
        };
    }, []);

    // --- RENDER ---
    return (
        <Box sx={{
            width: "100%",
            height: "88dvh",
            display: "flex",
            justifyContent: "center",
            p: 2,
            boxSizing: "border-box",
        }}>
            <ChatContainer>
                <MainContainer>
                    <ChatSection>
                        <MemoChatMessages messages={messages} parseHTML={parseHTML} chatRef={chatRef} />
                        <InputContainer>
                            <TextField
                                sx={{ width: "100%", "& textarea": { resize: "vertical" } }}
                                value={prompt}
                                onChange={handleInputChange}
                                onKeyDown={e => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                label={<TranslatedText text={"Type your message..."} />}
                                multiline
                                rows={2}
                                fullWidth
                                variant="outlined"
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSendMessage}
                                disabled={isStreaming || !chatConfig.aimodel}
                            >
                                {isStreaming ? "Streaming..." : <TranslatedText text={"Send"} />}
                            </Button>
                        </InputContainer>
                    </ChatSection>
                    <BlocksHeader collapsed={isBlocksCollapsed}>
                        <IconButton
                            size="small"
                            onClick={() => setIsBlocksCollapsed(v => !v)}
                            sx={{ bgcolor: "background.paper", boxShadow: 1, "&:hover": { bgcolor: "background.paper" } }}
                        >
                            {isBlocksCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                        {/* {!isBlocksCollapsed && (
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, opacity: 0.7 }}>
                                <TranslatedText text={"Activity"} />
                            </Typography>
                        )} */}
                    </BlocksHeader>
                    <BlocksSection collapsed={isBlocksCollapsed}>


                        {/* <CollapsedBar> только по необходимости! */}
                        <Collapse in={isBlocksCollapsed} timeout={0} unmountOnExit>

                            <CollapsedBar elevation={0}>
                                {blocks.length === 0 ? (
                                    <Tooltip title={<TranslatedText text={"No activity blocks yet"} />}>
                                        <Box sx={{ mt: 1, mb: 2, opacity: 0.6 }}>
                                            <InboxIcon fontSize="small" />
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    blocks.map((b) => (
                                        <Tooltip
                                            key={b.id}
                                            title={<Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{b.type}</Typography>
                                                <Typography variant="caption">{b.timestamp}</Typography>
                                            </Box>}
                                            placement="right"
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => setIsBlocksCollapsed(false)}
                                                aria-label={`open block ${b.type}`}
                                                sx={(theme) => ({ color: colorForBlockType(b.type, theme) })}
                                            >
                                                {iconForBlockType(b.type)}
                                            </IconButton>
                                        </Tooltip>
                                    ))
                                )}
                            </CollapsedBar>
                        </Collapse>
                        <Collapse in={!isBlocksCollapsed} timeout={0} unmountOnExit>
                            <BlocksContainer elevation={0} ref={blocksRef}>
                                {blocks.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            color: "text.secondary",
                                        }}
                                    >
                                        <TranslatedText text={"No activity blocks yet"} />
                                    </Box>
                                ) : (
                                    blocks.map((block, idx) => (
                                        <Appear
                                            key={block.id}
                                            id={block.id}
                                            delay={Math.min(idx * 150, 240)}
                                        >
                                            {renderBlock(block)}
                                        </Appear>
                                    ))
                                )}
                            </BlocksContainer>
                        </Collapse>
                    </BlocksSection>
                </MainContainer>
                {/* Нижняя панель управления */}
                <Stack direction="row" spacing={2} sx={{ px: 2, alignItems: "center", mt: 0 }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="ai-model-select"><TranslatedText text={"AI Model"} /></InputLabel>
                        <Select
                            labelId="ai-model-select"
                            value={chatConfig.aimodel}
                            label={<TranslatedText text={"AI Model"} />}
                            onChange={(e) => setChatConfig({ ...chatConfig, aimodel: e.target.value })}
                        >
                            {/* <MenuItem value={""}><TranslatedText text={"Not Selected"} /></MenuItem> */}
                            {aimodels.map((el) => (
                                <MenuItem key={el.id} value={el.name}>{el.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="ai-prompt-select"><TranslatedText text={"AI Prompt"} /></InputLabel>
                        <Select
                            labelId="ai-prompt-select"
                            value={chatConfig.aiprompt}
                            label={<TranslatedText text={"AI Prompt"} />}
                            onChange={(e) => setChatConfig({ ...chatConfig, aiprompt: e.target.value })}
                        >
                            <MenuItem value={""}><TranslatedText text={"Not Selected"} /></MenuItem>
                            {aiprompts.map((el: any) => (
                                <MenuItem key={el.id} value={el.id}>{el.code}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography><TranslatedText text={"HTML"} /></Typography>
                        <Switch onChange={() => setParseHTML(!parseHTML)} checked={parseHTML} />
                    </Box>
                    <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={handleClearHistory}
                        disabled={isStreaming}
                    >
                        <TranslatedText text={"Clear chat"} />
                    </Button>
                </Stack>
            </ChatContainer>
        </Box>
    );
}